import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { SITE, AIRPORTS } from "@/lib/site-data";
import { cityKeywords } from "@/lib/city-keywords";
import { getLanding, landingCopy, landingFaqs, LIMO_LANDINGS } from "@/lib/commercial-landing";
import { ldJson, faqJsonLd, hoursSpec24x7 } from "@/lib/seo-schema";
import { breadcrumbJsonLd } from "@/lib/breadcrumbs";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { AnswerBlock } from "@/components/AnswerBlock";
import { limoAnswer } from "@/lib/answer-first";
import { HourlyRates } from "@/components/HourlyRates";
import { ContactButtons } from "@/components/ContactButtons";
import { CTABand } from "@/components/CTA";
import heroImg from "@/assets/hero-chauffeur.jpg";
import { VideoShowcase } from "@/components/VideoShowcase";

const SITE_URL = "https://premierexecutivechauffeur.com";
const OG_IMAGE = `${SITE_URL}/og-default.jpg`;

export const Route = createFileRoute("/limo-service/$city")({
  loader: ({ params }) => {
    const found = getLanding(params.city);
    if (!found) throw notFound();
    return found;
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Unavailable" }, { name: "robots", content: "noindex" }] };
    }
    const { landing, city } = loaderData;
    const url = `${SITE_URL}/limo-service/${city.slug}`;
    const drive = landingCopy(landing, city).drive;
    const titleFull = `${landing.keyword} | Premier Executive Chauffeur`;
    const title = titleFull.length <= 60 ? titleFull : `${landing.keyword} | Premier Chauffeur`;
    const description = `${landing.keyword}: flat-rate ${landing.airport} transfers (about ${drive}), hourly from $130/hr in a sedan, SUV or Sprinter. staffed dispatch — reserve online.`.slice(0, 160);
    const answer = limoAnswer({
      name: city.name,
      keyword: landing.keyword,
      airportCode: landing.airport,
      driveTime: drive,
      corridors: landing.corridors,
    });
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "keywords", content: cityKeywords(city, "limo") },
        { property: "og:title", content: `${landing.keyword} — Sedan, SUV & Sprinter` },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:image", content: OG_IMAGE },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: `${landing.keyword} — Premier Executive Chauffeur` },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: OG_IMAGE },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        breadcrumbJsonLd([
          { name: "Home", path: "/" },
          { name: "Limo Service", path: "/limo-service" },
          { name: landing.keyword, path: `/limo-service/${city.slug}` },
        ]),
        {
          type: "application/ld+json" as const,
          children: ldJson({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "WebPage",
                "@id": url,
                url,
                name: title,
                description,
                abstract: answer.answer,
                inLanguage: "en-US",
                isPartOf: { "@id": `${SITE_URL}#website` },
                about: { "@id": `${SITE_URL}#business` },
                primaryImageOfPage: `${SITE_URL}/og-default.jpg`,
                breadcrumb: { "@id": `${url}#breadcrumb` },
                speakable: {
                  "@type": "SpeakableSpecification",
                  cssSelector: [".answer-first"],
                },
              },
              {
                "@type": "Service",
                "@id": `${url}#service`,
                name: landing.keyword,
                serviceType: "Limousine and chauffeur service",
                description,
                provider: { "@id": `${SITE_URL}#business` },
                areaServed: {
                  "@type": "City",
                  name: city.name,
                  address: {
                    "@type": "PostalAddress",
                    addressLocality: city.name,
                    addressRegion: "CA",
                    addressCountry: "US",
                  },
                },
                availableChannel: {
                  "@type": "ServiceChannel",
                  serviceUrl: url,
                  servicePhone: SITE.phone,
                  availableLanguage: "English",
                },
                hoursAvailable: hoursSpec24x7,
                offers: {
                  "@type": "Offer",
                  priceCurrency: "USD",
                  price: 130,
                  priceSpecification: {
                    "@type": "UnitPriceSpecification",
                    priceCurrency: "USD",
                    price: 130,
                    unitCode: "HUR",
                    referenceQuantity: {
                      "@type": "QuantitativeValue",
                      value: 5,
                      unitCode: "HUR",
                    },
                  },
                  availability: "https://schema.org/InStock",
                  url: SITE.bookUrl,
                },
              },
              {
                ...faqJsonLd(landingFaqs(landing, city)),
                "@id": `${url}#faq`,
                url,
                isPartOf: { "@id": `${SITE_URL}#website` },
                about: { "@id": `${SITE_URL}#business` },
              },
            ],
          }),
        },
      ],
    };
  },
  component: LimoCityLanding,
});

function LimoCityLanding() {
  const { landing, city } = Route.useLoaderData();
  const { paragraphs, drive } = landingCopy(landing, city);
  const faqs = landingFaqs(landing, city);
  const airport = AIRPORTS.find((a) => a.code === landing.airport);
  const others = LIMO_LANDINGS.filter((l) => l.slug !== landing.slug).slice(0, 5);
  const answer = limoAnswer({
    name: city.name,
    keyword: landing.keyword,
    airportCode: landing.airport,
    driveTime: drive,
    corridors: landing.corridors,
  });

  return (
    <>
      <section className="relative overflow-hidden border-b border-border">
        <img
          src={heroImg}
          alt={`Chauffeur holding the rear door of a black executive sedan for a ${city.name}, California limo service pickup`}
          width={1920}
          height={1080}
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/50 to-background" />
        <div className="relative container-x py-24 md:py-32 max-w-3xl">
          <Breadcrumbs
            items={[{ name: "Home", to: "/" }, { name: "Limo Service", to: "/limo-service" }, { name: city.name }]}
            className="mb-4"
          />
          <p className="eyebrow mb-4">{city.region}</p>
          <h1 className="text-4xl md:text-6xl mb-4 text-balance">
            {landing.keyword} — Sedan, SUV &amp; Sprinter Chauffeurs
          </h1>
          <p className="text-muted-foreground text-lg mb-6">
            Flat-rate {landing.airport} transfers about {drive} from {city.name}, hourly charters from $130/hr, and live dispatch desk.
          </p>
          <ContactButtons context={`${city.name} limo service`} />
        </div>
      </section>

      <div className="container-x py-16 space-y-16 max-w-3xl">
        <AnswerBlock content={answer} />

        <section className="space-y-4 text-muted-foreground">
          {paragraphs.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </section>

        <section>
          <h2 className="text-2xl md:text-3xl mb-4">Why {city.name} clients hire us</h2>
          <ul className="space-y-3 text-muted-foreground">
            {landing.demand.map((d: string) => (
              <li key={d} className="border-l-2 border-gold pl-4">
                {d}
              </li>
            ))}
          </ul>
          <p className="text-muted-foreground mt-4">
            Landmarks we serve most often: {city.landmarks.slice(0, 4).join(", ")}. Frequent hotel pickups include{" "}
            {city.hotels.slice(0, 3).join(", ")}.
          </p>
        </section>

        <HourlyRates
          heading={`${city.name} hourly chauffeur rates`}
          intro={`Published hourly pricing for ${city.name} charters. Every hourly reservation carries a five-hour minimum; tolls, parking, airport fees, and any pre-included gratuity are shown in the written quote before confirmation.`}
          showFaqs={false}
        />

        <section>
          <h2 className="text-2xl md:text-3xl mb-4">{city.name} limo service FAQs</h2>
          <dl className="space-y-6">
            {faqs.map((f) => (
              <div key={f.q}>
                <dt className="font-display text-lg text-foreground mb-1">{f.q}</dt>
                <dd className="text-muted-foreground">{f.a}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section>
          <h2 className="text-2xl md:text-3xl mb-4">Related pages</h2>
          <ul className="grid gap-3 sm:grid-cols-2 text-sm">
            <li>
              <Link to="/service-areas/$city" params={{ city: city.slug }} className="text-gold hover:underline">
                {city.name} chauffeur guide
                <span className="sr-only"> — full service-area page</span>
              </Link>
            </li>
            {airport && (
              <li>
                <Link to="/airports/$code" params={{ code: airport.slug }} className="text-gold hover:underline">
                  {airport.code} airport transfers
                  <span className="sr-only"> from {city.name}</span>
                </Link>
              </li>
            )}
            <li>
              <Link to="/sprinter-van-transportation" className="text-gold hover:underline">
                Sprinter van transportation
                <span className="sr-only"> — group travel from {city.name}</span>
              </Link>
            </li>
            <li>
              <Link to="/fleet" className="text-gold hover:underline">
                Vehicle fleet
                <span className="sr-only"> available in {city.name}</span>
              </Link>
            </li>
            <li>
              <Link to="/services/$service" params={{ service: "corporate" }} className="text-gold hover:underline">
                Corporate accounts
                <span className="sr-only"> for {city.name} companies</span>
              </Link>
            </li>
            <li>
              <Link to="/price-guide" className="text-gold hover:underline">
                Price guide
                <span className="sr-only"> for {city.name} bookings</span>
              </Link>
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl md:text-3xl mb-4">Limo service in nearby markets</h2>
          <ul className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
            {others.map((o) => (
              <li key={o.slug}>
                <Link to="/limo-service/$city" params={{ city: o.slug }} className="text-gold hover:underline">
                  {o.keyword}
                  <span className="sr-only"> — linked from {city.name}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <VideoShowcase
        heading={`Watch the ${landing.keyword.toLowerCase()} fleet in motion`}
        blurb={`Black luxury sedans, Chevrolet Suburban SUVs and Mercedes-Benz Sprinter vans — the vehicles and chauffeur standard behind every ${city.name} black car, limousine and chauffeured transportation booking.`}
        playLabel={`Play the fleet video on the ${landing.keyword} page`}
      />

      <CTABand
        heading={`Reserve a chauffeur in ${city.name}`}
        sub={`Written confirmation in minutes. Call ${SITE.phone} or request an instant quote online.`}
      />
    </>
  );
}
